import { Spinner } from "@via/components/loading/loading.tsx";
import { ROUTES } from "@via/utils/constants.tsx";
import { urlDataSearch } from "@via/utils/form-validators.tsx";
import { FileListFromWebRequestBody } from "common";
import { type JSX, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useUrlDataConnection } from "@via/hooks/useUrlDataConnection.tsx";

export const exampleUrlLink =
  "https://www.viafoundry.com/test_data/fastq_mouse";
// const exampleUrlLink2 =
//   "https://ftp.ncbi.nlm.nih.gov/geo/samples/GSM7990nnn/GSM7990534/suppl/";

export default function UrlDataConnector({
  onConnectorFormSubmit,
  isGetUrlFolderListLoading,
}): JSX.Element {
  const navigate = useNavigate();
  const { starterUrl } = useUrlDataConnection();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FileListFromWebRequestBody>({
    mode: "onChange",
    defaultValues: {
      url: starterUrl ? starterUrl : "",
    },
  });

  useEffect(() => {
    if (starterUrl) {
      setValue("url", starterUrl);
      handleSubmit(handleUrlDataSearchFormSubmit)();
    }
  }, [starterUrl]);

  const handleUrlDataSearchFormSubmit = async (
    payload: FileListFromWebRequestBody
  ) => {
    onConnectorFormSubmit(payload);
  };

  const setExampleUrl = () => {
    setValue("url", exampleUrlLink);
    handleSubmit(handleUrlDataSearchFormSubmit)();
  };

  const handleCancel = () => {
    navigate(ROUTES.DATA);
  };

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        handleSubmit(handleUrlDataSearchFormSubmit)(e);
      }}>
      <div className="space-y-6 pb-5 pt-3">
        <div>
          <div className="mt-2">
            <input
              {...register("url", urlDataSearch.url)}
              type="text"
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              placeholder="Web URL"
            />
            {errors.url && (
              <span className="text-xs text-rose-500">
                {errors.url.message}
              </span>
            )}
            <div className="flex flex-col gap-2 mt-4 text-sm font-normal text-gray-500">
              <div>
                Enter the full web address (including “http://” or “https://”).
                Foundry will use the provided URL to retrieve the list of files
                from that link.
              </div>

              <div className="flex gap-2">
                Example link:
                <button
                  type="button"
                  onClick={() => setExampleUrl()}
                  disabled={isGetUrlFolderListLoading}
                  className="font-bold">
                  {exampleUrlLink}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-shrink-0 justify-end py-4">
          <button
            type="button"
            data-testid="cloudConnectorCancelButton"
            onClick={handleCancel}
            disabled={isGetUrlFolderListLoading}
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
            Cancel
          </button>
          <button
            data-testid="cloudConnectorSubmitButton"
            type="submit"
            disabled={isGetUrlFolderListLoading}
            className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
            <Spinner loading={isGetUrlFolderListLoading}>Connect</Spinner>
          </button>
        </div>
      </div>
    </form>
  );
}
